'use client';

/* eslint-disable @typescript-eslint/no-explicit-any */
import { PrefetchLink, SafeImage, Text } from '@/ui';
import { validateImage } from '@lib';
import type { BannerComponentFields } from '@types';
import { clsx } from 'clsx';

import { DesktopRatios, MobileRatios } from '../../../lib/constants/desktop-ratios';
import { Button } from '../button';
import { ClientBackground } from './client-background';

export type BannerProps = React.HTMLAttributes<HTMLDivElement> &
  Omit<React.ComponentPropsWithoutRef<typeof PrefetchLink>, 'href'> &
  BannerComponentFields;

export function Banner({
  heading,
  subHeading,
  ratio,
  video,
  image,
  href,
  actions,
  layout,
  className,
  hideForeground = false,
  increaseNavigationContrast = false,
  blockWidth,
  contentAlignment,
  overlay,
  parallaxSpeed = 0,
  headingTag = 'h1',
  subHeadingTag = 'p',
  id,
  ...rest
}: BannerProps) {
  const props = href ? { ...rest, href } : { ...rest };
  const Wrapper = href ? PrefetchLink : 'div';

  return (
    <section
      id={id}
      className={clsx(
        'relative flex w-full justify-center',
        {
          'max-w-[theme(screens.3xl)] rounded-lg': blockWidth === 'desktop',
          'max-w-(--breakpoint-2xl) rounded-lg': blockWidth === 'wide',
          'max-w-(--breakpoint-xl) rounded-lg': blockWidth === 'standard',
          'max-w-(--breakpoint-lg) rounded-lg': blockWidth === 'narrow',
          'max-w-(--breakpoint-md) rounded-lg': blockWidth === 'prose',
        },
        {
          'rounded-lg p-4 sm:p-6': layout === 'padded',
        },
        ratio?.desktop ? DesktopRatios.get(ratio?.desktop) : DesktopRatios.get('widescreen-(16:9)'),
        ratio?.mobile ? MobileRatios.get(ratio?.mobile) : MobileRatios.get('portrait-(2:3)'),
        className,
      )}
    >
      {/* Background with nested dark overlay and optional image overlay */}
      <Wrapper
        {...(props as any)}
        className={clsx('bg-muted relative flex w-full items-center justify-center overflow-clip', {
          'max-w-(--breakpoint-2xl) rounded-sm': layout === 'padded',
        })}
      >
        {image?.desktop && (
          <ClientBackground
            parallaxSpeed={parallaxSpeed}
            image={image}
            video={video}
            increaseNavigationContrast={increaseNavigationContrast}
          />
        )}

        <div
          className={clsx(
            'flex h-full w-full flex-col items-center justify-center gap-2 transition ease-out',
            { 'cursor-pointer hover:bg-black/60': href },
            { 'z-10 bg-black/50': !hideForeground },
            { hidden: hideForeground },
          )}
        >
          {validateImage(overlay) && (
            <SafeImage
              src={overlay}
              fill
              priority
              sizes={'100vw'}
              className="inset-0 z-10 object-cover object-bottom"
            />
          )}
        </div>
      </Wrapper>

      {/* Content */}
      <div
        className={clsx(
          'pointer-events-none absolute flex h-full w-full max-w-(--breakpoint-xl) flex-col gap-2 px-4 py-16 text-white sm:px-8',
          {
            'items-start text-start': contentAlignment?.alignment?.horizontalAlignment === 'left',
            'items-center text-center':
              contentAlignment?.alignment?.horizontalAlignment === 'center' ||
              !contentAlignment?.alignment?.horizontalAlignment,
            'items-end text-end': contentAlignment?.alignment?.horizontalAlignment === 'right',
            'justify-start': contentAlignment?.alignment?.verticalAlignment === 'top',
            'justify-center':
              contentAlignment?.alignment?.verticalAlignment === 'middle' ||
              !contentAlignment?.alignment?.verticalAlignment,
            'justify-end': contentAlignment?.alignment?.verticalAlignment === 'bottom',
            hidden: hideForeground,
          },
        )}
      >
        <Text element={headingTag} elementStyle="h1" className="w-full max-w-2xl border-none p-0 text-balance">
          {heading}
        </Text>

        {subHeading && (
          <Text element={subHeadingTag} elementStyle={'p'} className={'mt-2 max-w-2xl text-balance sm:text-lg'}>
            {subHeading}
          </Text>
        )}

        {/* Actions */}
        {(actions?.primary || actions?.secondary) && (
          <div
            className={clsx('xs:flex-row z-10 flex min-w-0 flex-col-reverse flex-wrap gap-2 pt-3', {
              'justify-start': contentAlignment?.alignment?.horizontalAlignment === 'left',
              'justify-center':
                contentAlignment?.alignment?.horizontalAlignment === 'center' ||
                !contentAlignment?.alignment?.horizontalAlignment,
              'justify-end': contentAlignment?.alignment?.horizontalAlignment === 'right',
            })}
          >
            {actions?.secondary?.children && (
              <Button
                {...actions?.secondary}
                variant={'outline'}
                className={clsx(
                  'xs:w-auto pointer-events-auto w-full min-w-0 whitespace-pre-wrap',
                  actions.secondary.className,
                )}
              />
            )}
            {actions?.primary?.children && (
              <Button
                {...actions?.primary}
                className={clsx(
                  'xs:w-auto backdrop-blur-0 pointer-events-auto w-full min-w-0 gap-3 whitespace-pre-wrap transition hover:backdrop-blur-md',
                  actions.primary?.className,
                )}
              >
                {actions?.primary?.children}
              </Button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
